import type {
  Building,
  BuildingDesign,
} from '@shared/models/design/building-design.model';
import { isNameMatch } from '../string.helpers';

const BUILDING_NAME_PREFIX = 'Building ';

/** Spreadsheet-style building suffix for a zero-based index: A..Z, AA..AZ. */
export const getBuildingLetter = (index: number): string => {
  if (!Number.isFinite(index) || index < 0) return '';
  let remaining = Math.floor(index);
  let letter = '';
  while (remaining >= 0) {
    letter = String.fromCharCode(65 + (remaining % 26)) + letter;
    remaining = Math.floor(remaining / 26) - 1;
  }
  return letter;
};

/** Automatic persisted name for a building at the given zero-based index. */
export const getDefaultBuildingName = (index: number): string =>
  `${BUILDING_NAME_PREFIX}${getBuildingLetter(index)}`;

/**
 * Name assigned to the next building. Like version naming, allocation starts
 * from the resulting building count and advances until that name is free.
 */
export const getNextBuildingName = (
  buildings: readonly Pick<Building, 'name'>[],
): string => {
  for (let index = buildings.length; ; index += 1) {
    const candidate = getDefaultBuildingName(index);
    if (!buildings.some((building) => isNameMatch(building.name, candidate))) {
      return candidate;
    }
  }
};

/**
 * Persist the automatic labels previously derived from array position.
 * Existing names are untouched so custom names and already allocated defaults
 * remain stable when another building is removed.
 */
export const withPersistentBuildingNames = (
  design: BuildingDesign,
): BuildingDesign => {
  let changed = false;
  const buildings = design.buildings.map((building, index) => {
    if (building.name?.trim()) return building;
    changed = true;
    return { ...building, name: getDefaultBuildingName(index) };
  });

  return changed ? { ...design, buildings } : design;
};

/** User-facing building name, with an index fallback for incomplete data. */
export const getBuildingDisplayName = (
  building: Pick<Building, 'name'>,
  index: number,
): string => building.name?.trim() || getDefaultBuildingName(index);
